import { ChevronDown, ChevronUp } from "lucide-react";
import { useState } from "react";

import type { IntradayCumulativePoint } from "../../lib/intradayPnl";
import { formatMoney, formatSignedMoney } from "../../lib/money";
import { formatTimeShort } from "../../lib/date";
import { Card } from "../ui/card";
import { IntradayPnlChart } from "./IntradayPnlChart";

// "Running P&L" session card for the single-day Trades drill-in: a headline
// close figure with a session up/down pill, a left stat rail (Open / High /
// Low / Close / Range / Trades), and the intraday running-P&L chart. All
// figures derive from the same cumulative series the chart draws, so the rail,
// the headline, and the curve's close always agree.

interface RunningPnlPanelProps {
  points: ReadonlyArray<IntradayCumulativePoint>;
  currency: string;
  dayLabel: string;
  loading?: boolean;
}

function toneClass(value: number): string {
  if (value > 0) return "text-success";
  if (value < 0) return "text-danger";
  return "text-on-surface";
}

export function RunningPnlPanel({
  points,
  currency,
  dayLabel,
  loading = false,
}: RunningPnlPanelProps) {
  // Trade under the cursor (null when idle) — drives the live headline figure.
  const [hovered, setHovered] = useState<IntradayCumulativePoint | null>(null);

  const cumulatives = points.map((point) => point.cumulative);
  // The day opens flat at $0, so the high/low brackets include that baseline.
  const high = Math.max(0, ...cumulatives);
  const low = Math.min(0, ...cumulatives);
  const close = points.at(-1)?.cumulative ?? 0;
  const range = high - low;
  const tradeCount = points.length;

  // The big figure tracks the hovered point's running total, falling back to
  // the day's close when the cursor is off the plot.
  const headlineValue = hovered?.cumulative ?? close;
  const positive = headlineValue >= 0;
  const headlineCaption = hovered !== null ? formatTimeShort(hovered.closedAt) : "session";

  const rows: ReadonlyArray<{ label: string; value: string; tone: string }> = [
    { label: "Open", value: formatMoney("0", { currency }), tone: "text-on-surface" },
    {
      label: "High",
      value: formatSignedMoney(high.toFixed(2), { currency }),
      tone: high > 0 ? "text-success" : "text-on-surface",
    },
    {
      label: "Low",
      value: formatSignedMoney(low.toFixed(2), { currency }),
      tone: low < 0 ? "text-danger" : "text-on-surface",
    },
    {
      label: "Close",
      value: formatSignedMoney(close.toFixed(2), { currency }),
      tone: toneClass(close),
    },
    { label: "Range", value: formatMoney(range.toFixed(2), { currency }), tone: "text-on-surface" },
    { label: "Trades", value: String(tradeCount), tone: "text-on-surface" },
  ];

  return (
    <Card className="p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-on-surface-variant">
            Running P&amp;L
          </div>
          <div className="mt-1 flex items-center gap-2">
            <span
              className={`font-data text-3xl font-semibold leading-none tracking-tight ${toneClass(headlineValue)}`}
            >
              {tradeCount > 0 ? formatSignedMoney(headlineValue.toFixed(2), { currency }) : "—"}
            </span>
            {tradeCount > 0 ? (
              <span
                className={`inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 text-xs font-medium tabular-nums ${
                  positive ? "bg-success-soft text-success" : "bg-danger-soft text-danger"
                }`}
              >
                {positive ? (
                  <ChevronUp className="h-3 w-3" aria-hidden="true" />
                ) : (
                  <ChevronDown className="h-3 w-3" aria-hidden="true" />
                )}
                {headlineCaption}
              </span>
            ) : null}
          </div>
        </div>
        {dayLabel !== "" ? (
          <div className="shrink-0 text-xs text-on-surface-variant">{dayLabel}</div>
        ) : null}
      </div>

      <div className="mt-4 flex flex-col gap-5 sm:flex-row">
        <dl className="w-full shrink-0 sm:w-44">
          {rows.map((row) => (
            <div
              key={row.label}
              className="flex items-center justify-between border-b border-outline-variant py-1.5 text-sm last:border-b-0"
            >
              <dt className="text-on-surface-variant">{row.label}</dt>
              <dd className={`font-data tabular-nums ${row.tone}`}>{row.value}</dd>
            </div>
          ))}
        </dl>
        <div className="h-52 min-w-0 flex-1">
          {loading ? (
            <p className="text-sm text-on-surface-variant">Loading…</p>
          ) : (
            <IntradayPnlChart points={points} currency={currency} onActiveChange={setHovered} />
          )}
        </div>
      </div>
    </Card>
  );
}
